import { ptBR } from 'date-fns/locale';

import { formatDateWithPattern } from '@/utils/date-time/formatDateWithPattern';

const THREAT_DATETIME_PATTERN = 'dd/MM/yyyy, HH:mm:ss';

const DEFAULT_MOBILE_PATH_DISPLAY_LENGTH = 35;

/**
 * Shortens long file paths for compact mobile threat cards.
 */
export const ellipsisThreatPathForMobile = (
  path: string,
  maxLength = DEFAULT_MOBILE_PATH_DISPLAY_LENGTH,
): string => {
  if (path.length <= maxLength) {
    return path;
  }
  return `${path.slice(0, maxLength)}...`;
};

/**
 * Formats an ISO timestamp for the threats table (`pt-BR`-style date + time).
 * Falls back to the raw `iso` string when parsing/formatting fails.
 */
export const formatThreatDateTime = (iso: string): string => {
  const formatted = formatDateWithPattern(iso, THREAT_DATETIME_PATTERN, { locale: ptBR });
  return formatted || iso;
};

/** Maps API `status` strings to gatorcomponents `Tag` variants. */
export type ThreatSituationTagVariant = 'pending' | 'success' | 'neutral';

export type ThreatSituationTag = {
  label: string;
  variant: ThreatSituationTagVariant;
};

/** Localized short labels for known threat statuses (from `gatorProtect.threatsTable.situationTags`). */
export type ThreatSituationTagLabels = {
  quarantine: string;
  remediated: string;
};

const THREAT_STATUS = {
  quarantined: 'quarantined',
  remediated: 'remediated',
  clean: 'clean',
  resolved: 'resolved',
} as const;

/**
 * Maps backend `status` to a `Tag` label + variant (quarantine → pending, remediated/clean/resolved → success).
 * Known states use `labels` from locale; unknown values use `variant: 'neutral'` and the raw `status` as label.
 */
export const resolveThreatSituationTag = (
  status: string,
  labels: ThreatSituationTagLabels,
): ThreatSituationTag => {
  const s = status.toLowerCase();
  if (s === THREAT_STATUS.quarantined) {
    return { label: labels.quarantine, variant: 'pending' };
  }
  if (
    s === THREAT_STATUS.remediated
    || s === THREAT_STATUS.clean
    || s === THREAT_STATUS.resolved
  ) {
    return { label: labels.remediated, variant: 'success' };
  }
  return { label: status, variant: 'neutral' };
};
